import { mapGetters, mapActions } from 'vuex';
import {
    compileSegmentPipeline,
    formatMetadataForRules,
    getRules,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { filtersHaveRulesOfKind } from '@/vuex/modules/segments/segment.helpers';
import { nestedRuleCount, hasCSVRuleOperator } from '@/utils/segments.js';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';

const SegmentEditing = {
    provide () {
        return {
            segmentsUsedCount: () => this.segmentsUsedCount,
        };
    },
    computed: {
        ...mapGetters({
            hasSegmentFlag: 'auth/hasSegmentFlag',
            getFeatureFlag: 'subscription/getFeatureFlag',
            metadata: 'metadata/metadataList',
            sessionProperties: 'metadata/sessionProperties',
            metadataListByKind: 'metadata/metadataListByKind',
        }),
        advancedOptions () {
            return get(this.localSegment, 'definition.advancedOptions', {});
        },
        disableAnonymousOption () {
            const filters = this.localSegment?.definition?.filters || [];
            const hasRealFilters = filters?.some(({ kind, or }) => kind || or?.some(({ kind }) => kind));

            return (
                hasRealFilters &&
                (filtersHaveRulesOfKind(filters, 'segment') || !filtersHaveRulesOfKind(filters, 'visitor'))
            );
        },
        showAnonymousEnabled () {
            return this.getFeatureFlag('showAnonymous');
        },
        segmentsUsedCount () {
            return nestedRuleCount(this.localSegment);
        },
        hasCSVRule () {
            return hasCSVRuleOperator(this.localSegment);
        },
    },
    methods: {
        ...mapActions({
            loadAllApps: 'apps/loadAll',
            loadAllPages: 'pages/loadAll',
            loadAllFeatures: 'features/loadAll',
            loadAllTrackTypes: 'trackTypes/loadAll',
            loadAllGuides: 'guides/loadAll',
            loadAllJourneys: 'journeys/loadAllJourneys',
            loadAllMetadata: 'metadata/loadAll',
            loadAllFeedbackRequests: 'feedbackRequests/loadAll',
            loadAllEmails: 'emails/loadAll',
        }),
        async initLocalSegment (useVuexSegments = true) {
            if (useVuexSegments) {
                this.localSegment = cloneDeep(this.editor.segment);
            }

            this.updateSegmentAdvancedOptions(this.localSegment.definition.advancedOptions);
            const requests = [this.loadAllApps(), this.loadAllMetadata(), this.loadRequiredResources()];

            await Promise.all(requests);

            const metadata = [...this.metadata, ...this.sessionProperties];

            const metadataForRules = formatMetadataForRules(metadata);
            const { rules } = getRules(metadataForRules, {
                guideFriendly: this.onlyAllowGuideCompatibleRules,
                targetAppId: this.targetAppId,
            });

            const config = {
                rules,
                onlyAllowGuideCompatibleRules: this.onlyAllowGuideCompatibleRules,
            };

            if (!this.onlyAllowGuideCompatibleRules) {
                config.eventProperties = this.metadataListByKind('event');
                config.sessionProperties = this.sessionProperties;
            }

            this.config = config;

            if (!this.isBuiltSegment && get(this.localSegment.definition, 'filters.length')) {
                this.isCopy = true;
            }

            await this.$nextTick();
            this.ready = true;
        },
        async loadRequiredResources () {
            // only fetch resources required to display intiial ruleset to improve load performance
            // additional resources are fetched on demand based on user selection
            const { filters } = this.localSegment.definition;
            if (!filters.length) {
                return;
            }

            // flatten all the filters for the current segment and return array of ids
            // ['visitor.auto.name', 'page', 'guide', '...']
            const filterIds = filters
                .reduce((acc, filter) => {
                    if (filter.or) {
                        acc.push(...filter.or);
                    } else {
                        acc.push(filter);
                    }

                    return acc;
                }, [])
                .map((filter) => filter.id);

            const requestsMap = {
                loadAllPages: filterIds.includes('page'),
                loadAllFeatures: filterIds.includes('feature'),
                loadAllTrackTypes: filterIds.includes('trackType'),
                loadAllGuides: filterIds.includes('guide'),
                loadAllJourneys: filterIds.includes('journey'),
                loadAllFeedbackRequests: filterIds.includes('feedbackRequest'),
                loadAllEmails: filterIds.includes('email'),
            };

            // loop through requestsMap and conditionally fetch the resource
            // if resource isn't needed, resolve the promise
            const requests = Object.keys(requestsMap).map((request) => {
                if (requestsMap[request]) {
                    return this[request]();
                }

                return Promise.resolve();
            });

            await Promise.all(requests);
        },
        async updateSegmentAdvancedOptions (advancedOptions = {}, updatePipeline = true) {
            if (this.disableAnonymousOption) {
                advancedOptions.selectedVisitorType = null;
            }

            if (!this.isReplay) {
                advancedOptions.showGuidesToAnon = this.showGuidesToAnon;
            }

            this.$set(this.localSegment.definition, 'advancedOptions', advancedOptions);

            if (updatePipeline) {
                await this.updateSegmentPipeline();
            }
        },
        async updateSegmentFilters (filters) {
            this.isValid = filters.every((rule) => {
                if (rule.or) {
                    return rule.or.every((rule) => !!rule.$valid);
                }

                return !!rule.$valid;
            });

            if (this.isValid) {
                this.checkValidRule = false;
            }

            this.$set(this.localSegment.definition, 'filters', filters);

            this.updateSegmentAdvancedOptions(this.localSegment.definition.advancedOptions, false);
            await this.updateSegmentPipeline();
        },
        async updateSegmentPipeline () {
            const pipelineProps = await compileSegmentPipeline(this.localSegment);
            this.$set(this.localSegment, 'compound', pipelineProps?.compound);
            this.$set(this.localSegment, 'pipeline', pipelineProps?.pipeline);
            this.$set(this.localSegment, 'pipelines', pipelineProps?.pipelines);
        },
    },
};

export default SegmentEditing;
